'use client'
import { useRef, type FC } from 'react'
import styled from 'styled-components'
import { motion, useInView } from 'motion/react'
import { mq, presetsFluid, rgbAlpha, VARIANTS_FADE_FROM_LEFT_TO_RIGHT, vw } from '@/styles'
import { SectionMediumFullWidth } from './project/SectionMediumFullWidth'
import type { TypeImageLocal } from '@/schema'
import { ListInfoRows } from './ListInfoRows'

type Props = {
  data: {
    title: string
    articles: {
      title: string
      description: string
      medium?: TypeImageLocal
    }[]
  }
}

const headingId = 'main'
const h2Props = {
  initial: 'initial',
  exit: 'exit',
  variants: VARIANTS_FADE_FROM_LEFT_TO_RIGHT({})
}

const headerVideo = {
  type: 'video' as const,
  src: '/media/homepage/header.mp4', // PENDINGPROPER ROUTE
  alt: 'Facilities header video'
}

export const MainFacilities:FC<Props> = ({ data }) => {
  const { title, articles } = data
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { margin: '3px', once: true })

  return (
    <Main>
      <SectionMediumFullWidth data={headerVideo} />
      <Section aria-labelledby={headingId} ref={ref}>
        <h2 id={headingId}>
          <motion.span {...h2Props} animate={isInView ? 'animate' : 'initial'}>
            {title}
          </motion.span>
        </h2>
        <ListInfoRows data={articles} />
      </Section>
    </Main>
  )
}

const Main = styled.main`
  background-color: var(--color-background--dark);
  color: var(--color-text--dark);
  grid-column: 1 / span 6;
  width: var(--viewport-width);
  width: var(--viewport-width--dynamic);

  ${mq.greaterThan('tablet')} {
    grid-column: 1 / span 12;
  }
`

const Section = styled.section`
  ${mq.greaterThan('tablet')} {
    min-height: 100vh;
    position: relative;
  }

  h2 {
    ${presetsFluid.font35_50()}
		max-width: 800px;
    padding: ${vw(20, 'mobile')} ${vw(10, 'mobile')} ${vw(60, 'mobile')} ${vw(10, 'mobile')};
    text-align: left;
    z-index: 3;

    ${mq.greaterThan('nexus7')} {
      padding: ${vw(20, 'tablet')} ${vw(10, 'tablet')} ${vw(60, 'tablet')} ${vw(10, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      align-self: flex-start;
      padding: ${vw(30, 'desktop')} ${vw(30, 'desktop')} ${vw(150, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      ${presetsFluid.font50_rel()}
      font-weight: 400;
      padding: 30px 30px 150px ${vw(30, 'desktop')};
    }
  }

  h2,
  .heading {
    ${mq.greaterThan('desktop-xs')} {
      width: var(--viewport-width);
      width: var(--viewport-width--dynamic);
    }
  }

	dl {
		border-top: 1px solid ${rgbAlpha('--color-text--dark', .2)};
	}
`
